import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Calendar, 
  PieChart, 
  Activity,
  RefreshCw,
  Download,
  Filter,
  Eye,
  Target,
  Award
} from 'lucide-react';

const Analytics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await api.get('/enquiries/statistics');
      setStats(response.data.data);
    } catch (error) {
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, trend, trendValue, description }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-soft border border-gray-100 dark:border-gray-700 hover:shadow-soft-lg transition-all duration-300">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        {trend && (
          <div className={`flex items-center text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            <TrendingUp className="w-4 h-4 mr-1" />
            {trendValue}%
          </div>
        )}
      </div>
      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{value}</h3>
      <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">{title}</p>
      {description && (
        <p className="text-gray-500 dark:text-gray-500 text-xs mt-1">{description}</p>
      )}
    </div>
  );

  const ChartCard = ({ title, children, className = "" }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-soft border border-gray-100 dark:border-gray-700 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        <div className="flex items-center space-x-2">
          <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <Download className="w-4 h-4" />
          </button>
          <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <Eye className="w-4 h-4" />
          </button>
        </div>
      </div>
      {children}
    </div>
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <RefreshCw className="w-12 h-12 animate-spin mb-4 text-primary-600" />
        <p className="text-lg text-gray-700 dark:text-gray-300">Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 text-red-700 px-4 py-3 rounded-xl text-center text-sm border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
        {error}
      </div>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Analytics</h1>
          <p className="text-gray-600 dark:text-gray-400">Comprehensive insights into your enquiry data</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          
          <button
            onClick={fetchAnalytics}
            className="btn btn-outline btn-md flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Enquiries"
          value={stats?.total || 0}
          icon={Users}
          color="bg-blue-500"
          trend="up"
          trendValue={12}
          description="All time enquiries"
        />
        <StatCard
          title="Conversion Rate"
          value={`${stats?.enrolled && stats?.total ? Math.round((stats.enrolled / stats.total) * 100) : 0}%`}
          icon={Target}
          color="bg-green-500"
          trend="up"
          trendValue={8}
          description="Enrolled vs Total"
        />
        <StatCard
          title="Response Rate"
          value={`${stats?.contacted && stats?.total ? Math.round((stats.contacted / stats.total) * 100) : 0}%`}
          icon={Activity}
          color="bg-purple-500"
          trend="up"
          trendValue={15}
          description="Contacted enquiries"
        />
        <StatCard
          title="Avg Response Time"
          value="2.4h"
          icon={Calendar}
          color="bg-orange-500"
          trend="down"
          trendValue={5}
          description="Time to first contact"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Enquiry Trends */}
        <ChartCard title="Enquiry Trends">
          <div className="h-64 flex items-center justify-center bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Chart visualization would go here</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">Using libraries like Chart.js or Recharts</p>
            </div>
          </div>
        </ChartCard>

        {/* Status Distribution */}
        <ChartCard title="Status Distribution">
          <div className="h-64 flex items-center justify-center bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="text-center">
              <PieChart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Pie chart would show status breakdown</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">Pending, Contacted, Enrolled, Not Interested</p>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Performing Courses */}
        <ChartCard title="Top Performing Courses" className="lg:col-span-2">
          <div className="space-y-4">
            {[
              { course: 'Web Development', enquiries: 45, conversion: 78 },
              { course: 'Data Science', enquiries: 32, conversion: 65 },
              { course: 'Mobile Development', enquiries: 28, conversion: 72 },
              { course: 'Machine Learning', enquiries: 22, conversion: 68 },
              { course: 'DevOps', enquiries: 18, conversion: 55 }
            ].map((item, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center mr-3">
                    <Award className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{item.course}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{item.enquiries} enquiries</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-green-600 dark:text-green-400">{item.conversion}%</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">conversion</p>
                </div>
              </div>
            ))}
          </div>
        </ChartCard>

        {/* Recent Activity */}
        <ChartCard title="Recent Activity">
          <div className="space-y-4">
            {[
              { action: 'New enquiry received', time: '2 min ago', type: 'enquiry' },
              { action: 'Status updated to Contacted', time: '15 min ago', type: 'update' },
              { action: 'Enquiry enrolled', time: '1 hour ago', type: 'enrolled' },
              { action: 'New enquiry received', time: '2 hours ago', type: 'enquiry' },
              { action: 'Status updated to Not Interested', time: '3 hours ago', type: 'update' }
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className={`w-2 h-2 rounded-full mt-2 ${
                  item.type === 'enquiry' ? 'bg-blue-500' :
                  item.type === 'update' ? 'bg-yellow-500' :
                  'bg-green-500'
                }`}></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{item.action}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.time}</p>
                </div>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
    </AdminLayout>
  );
};

export default Analytics; 